"use client";

import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

import { cn } from "@/lib/utils";

// Container for tooltips that must appear above modals (high z-index, pointer-events: none so clicks pass through)
const TOOLTIP_ABOVE_MODAL_CONTAINER_ID = "tooltip-above-modal-container";

function getTooltipAboveModalContainer(): HTMLElement {
  if (typeof document === "undefined") {
    throw new Error("getTooltipAboveModalContainer requires document (client-side only)");
  }
  let el = document.getElementById(TOOLTIP_ABOVE_MODAL_CONTAINER_ID);
  if (!el) {
    el = document.createElement("div");
    el.id = TOOLTIP_ABOVE_MODAL_CONTAINER_ID;
    el.style.cssText =
      "position:fixed;inset:0;z-index:2147483647;pointer-events:none;";
    document.body.appendChild(el);
  }
  return el;
}

function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  );
}

function Tooltip({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return (
    <TooltipProvider>
      <TooltipPrimitive.Root data-slot="tooltip" {...props} />
    </TooltipProvider>
  );
}

function TooltipTrigger({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

interface TooltipContentProps extends React.ComponentProps<typeof TooltipPrimitive.Content> {
  container?: HTMLElement | null;
}

function TooltipContent({
  className,
  sideOffset = 0,
  children,
  container,
  ...props
}: TooltipContentProps) {
  const portalContainer = container ?? undefined;
  return (
    <TooltipPrimitive.Portal container={portalContainer}>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          "bg-foreground text-background animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-fit origin-(--radix-tooltip-content-transform-origin) rounded-md px-3 py-1.5 text-xs text-balance",
          className
        )}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow className="bg-foreground fill-foreground z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]" />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

interface TooltipWithContentProps extends Omit<React.ComponentProps<typeof Tooltip>, "content"> {
  content: React.ReactNode;
  children: React.ReactNode;
  position?: "top" | "right" | "bottom" | "left";
  sideOffset?: number;
  className?: string;
  /** When true, portals tooltip into a high-z-index container so it appears above modals */
  aboveModal?: boolean;
  /** Portal the tooltip into this element (e.g. modal dialog) so it renders inside and above that container */
  tooltipContainer?: HTMLElement | null;
}

function TooltipWithContent({
  content,
  children,
  position,
  sideOffset,
  className,
  aboveModal = false,
  tooltipContainer,
  ...tooltipProps
}: TooltipWithContentProps) {
  const container =
    tooltipContainer ?? (aboveModal ? getTooltipAboveModalContainer() : undefined);
  return (
    <Tooltip {...tooltipProps}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent
        side={position}
        sideOffset={sideOffset}
        className={className}
        container={container}
      >
        {content}
      </TooltipContent>
    </Tooltip>
  );
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, TooltipWithContent };
